import { LeasingCalcInputType } from "../leasingCalcTypes";
import { InsuranceCalcMethodType, InsuranceInputType, InsuranceOutputType } from "./insuranceCalcTypes";

const insuranceCalcTryg: InsuranceCalcMethodType = ({
  insuranceInput: input,
  valueConfig: config,
}, isPrivate, isInactivePeriod, isVan, isElectric) => {
  const { active, vehiclePrice, glassDamage, roadAssistance } = input;

  const insuranceAddOns = getInsuranceAddons(input);
  const insuranceClasses = getInsuranceClasses(isVan, !!isElectric, isInactivePeriod)

  //Init output
  let output: InsuranceOutputType = {
    insurable: false,
    monthlyPayment: null,
    ownRisk: null,
    roadAssistance: insuranceAddOns.roadHelp,
    glassDamage: insuranceAddOns.glassDamage,
    roadAssistanceMonthlyPayment: Math.ceil(insuranceAddOns.roadHelp / 12 / 10) * 10,
    glassDamageMonthlyPayment: Math.ceil(insuranceAddOns.glassDamage / 12 / 10) * 10,
    glassDamageSelfRisk: insuranceAddOns.glassDamageSelfRisk,
  };

  if (!active || !vehiclePrice) return output;

  // Calculation
  for (let insuranceClass of insuranceClasses) {
    if ((vehiclePrice || 0) <= insuranceClass.amount) {
      output.monthlyPayment = insuranceClass.price;
      output.ownRisk = insuranceClass.selfRiskPrice;
      output.insurable = true
      break;
    }
  }


  if (output.insurable) {
    const INSURANCE_DAMAGE_TAX_RATE = 0.011;

    const insuranceCommission = config.insuranceCommission.value || 0;
    const monthlyInsuranceCommission = insuranceCommission / 12;

    const totalPayment = (output.monthlyPayment || 0)
    let taxedMonthlyPayment = totalPayment / 12;
    const damageInsuranceTax = taxedMonthlyPayment * INSURANCE_DAMAGE_TAX_RATE;
    taxedMonthlyPayment += monthlyInsuranceCommission;
    taxedMonthlyPayment += damageInsuranceTax;

    output.monthlyPayment = Math.ceil(taxedMonthlyPayment / 10) * 10;


    if (roadAssistance) output.monthlyPayment += output.roadAssistanceMonthlyPayment;
    if (glassDamage) {
      output.monthlyPayment += output.glassDamageMonthlyPayment;
    }
  }

  return output;
};

export default insuranceCalcTryg;

const fossilCarInsuranceClasses = [
  { amount: 50000, price: 5200, selfRiskPrice: 7500 },
  { amount: 100000, price: 5500, selfRiskPrice: 7500 },
  { amount: 150000, price: 5800, selfRiskPrice: 7500 },
  { amount: 200000, price: 6000, selfRiskPrice: 7500 },
  { amount: 250000, price: 6300, selfRiskPrice: 7500 },
  { amount: 300000, price: 6800, selfRiskPrice: 7500 },
  { amount: 350000, price: 7100, selfRiskPrice: 7500 },
  { amount: 400000, price: 7400, selfRiskPrice: 7500 },
  { amount: 450000, price: 7900, selfRiskPrice: 7500 },
  { amount: 500000, price: 8300, selfRiskPrice: 7500 },
  { amount: 550000, price: 9000, selfRiskPrice: 10000 },
  { amount: 600000, price: 9500, selfRiskPrice: 10000 },
];

const electricCarInsuranceClasses = [
  { amount: 50000, price: 5400, selfRiskPrice: 7500 },
  { amount: 100000, price: 5600, selfRiskPrice: 7500 },
  { amount: 150000, price: 5850, selfRiskPrice: 7500 },
  { amount: 200000, price: 6100, selfRiskPrice: 7500 },
  { amount: 250000, price: 6450, selfRiskPrice: 7500 },
  { amount: 300000, price: 7000, selfRiskPrice: 7500 },
  { amount: 350000, price: 7200, selfRiskPrice: 7500 },
  { amount: 400000, price: 7600, selfRiskPrice: 7500 },
  { amount: 450000, price: 8100, selfRiskPrice: 7500 },
  { amount: 500000, price: 8500, selfRiskPrice: 7500 },
  { amount: 550000, price: 9200, selfRiskPrice: 10000 },
  { amount: 600000, price: 9700, selfRiskPrice: 10000 },
];

const fossilVanInsuranceClasses = [
  { amount: 50000, price: 5400, selfRiskPrice: 7500 },
  { amount: 100000, price: 5700, selfRiskPrice: 7500 },
  { amount: 150000, price: 6100, selfRiskPrice: 7500 },
  { amount: 200000, price: 6400, selfRiskPrice: 7500 },
  { amount: 250000, price: 6750, selfRiskPrice: 7500 },
  { amount: 300000, price: 7100, selfRiskPrice: 7500 },
  { amount: 350000, price: 7400, selfRiskPrice: 7500 },
  { amount: 400000, price: 7700, selfRiskPrice: 7500 },
  { amount: 450000, price: 8200, selfRiskPrice: 7500 },
  { amount: 500000, price: 8600, selfRiskPrice: 7500 },
  { amount: 550000, price: 9200, selfRiskPrice: 10000 },
  { amount: 600000, price: 9600, selfRiskPrice: 10000 },
];

const electricVanInsuranceClasses = [
  { amount: 50000, price: 5500, selfRiskPrice: 7500 },
  { amount: 100000, price: 5850, selfRiskPrice: 7500 },
  { amount: 150000, price: 6300, selfRiskPrice: 7500 },
  { amount: 200000, price: 6550, selfRiskPrice: 7500 },
  { amount: 250000, price: 6900, selfRiskPrice: 7500 },
  { amount: 300000, price: 7300, selfRiskPrice: 7500 },
  { amount: 350000, price: 7550, selfRiskPrice: 7500 },
  { amount: 400000, price: 7800, selfRiskPrice: 7500 },
  { amount: 450000, price: 8300, selfRiskPrice: 7500 },
  { amount: 500000, price: 8800, selfRiskPrice: 7500 },
  { amount: 550000, price: 9400, selfRiskPrice: 10000 },
  { amount: 600000, price: 10000, selfRiskPrice: 10000 },
];

const standstillInsuranceClasses = [
  { amount: 50000, price: 3500, selfRiskPrice: 7500 },
  { amount: 100000, price: 4100, selfRiskPrice: 7500 },
  { amount: 150000, price: 4600, selfRiskPrice: 7500 },
  { amount: 200000, price: 5200, selfRiskPrice: 7500 },
  { amount: 250000, price: 5600, selfRiskPrice: 7500 },
  { amount: 300000, price: 5800, selfRiskPrice: 7500 },
  { amount: 350000, price: 6200, selfRiskPrice: 7500 },
  { amount: 400000, price: 6600, selfRiskPrice: 7500 },
  { amount: 450000, price: 7200, selfRiskPrice: 7500 },
  { amount: 500000, price: 7800, selfRiskPrice: 7500 },
  { amount: 550000, price: 8500, selfRiskPrice: 7500 },
  { amount: 600000, price: 9000, selfRiskPrice: 7500 },
];

function getInsuranceClasses(isVan: boolean, isElectric: boolean, inactivePeriod: boolean) {

  if (inactivePeriod) {
    return standstillInsuranceClasses;
  }

  if (!isVan && !isElectric) {
    return fossilCarInsuranceClasses;
  }
  if (!isVan && isElectric) {
    return electricCarInsuranceClasses;
  }
  if (isVan && !isElectric) {
    return fossilVanInsuranceClasses;
  }
  if (isVan && isElectric) {
    return electricVanInsuranceClasses;
  }
  return [];
}

function getInsuranceAddons({ vehiclePrice }: InsuranceInputType) {
  const insuranceAddOns = {
    roadHelp: 750,
    glassDamage: (vehiclePrice || 0) <= 500000 ? 950 : 1450,
    glassDamageSelfRisk: (vehiclePrice || 0) <= 500000 ? 3000 : 5500,
  };

  return insuranceAddOns
}
